import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import BASE_URL from "../../../config/apiConfig";
import { saveUserToken } from "../../../utils/auth";

export function CreateProfileViewPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract email from state or redirect if missing
  const store_email = location.state?.email;

  useEffect(() => {
    if (!store_email) {
      navigate("/get-otp");
    }
  }, [store_email, navigate]);

  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ phone: "", password: "" });
  const [serverError, setServerError] = useState("");

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^[0-9]{10}$/;
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!phoneRegex.test(phone)) {
      newErrors.phone = "Phone number must be exactly 10 digits.";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required.";
    } else if (!strongPasswordRegex.test(password)) {
      newErrors.password =
        "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/profile-setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: store_email, phone, password }),
      });

      if (response.ok) {
        const { token } = await response.json();
        saveUserToken(token, store_email);
        navigate("/user/home");
      } else {
        const errorData = await response.json();
        setServerError(errorData.message || "Profile setup failed.");
      }
    } catch (error) {
      setServerError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(to right, #f8fafc, #edf2fb)",
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          padding: 4,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Update Your Profile
        </Typography>

        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleUpdateProfile}
          display="flex"
          flexDirection="column"
        >
          <TextField
            fullWidth
            label="Email Address"
            value={store_email}
            InputProps={{ readOnly: true }}
            sx={{ mb: 3 }}
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} // Removes non-numeric input
            onInput={(e) =>
              (e.target.value = e.target.value.replace(/\D/g, ""))
            } // Ensures real-time restriction
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{ mb: 3 }}
            variant="outlined"
            type="tel"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 10,
            }} // Restricts to 10 digits
          />

          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            variant="outlined"
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
            sx={{
              backgroundColor: "#51833a",
              color: "#fff",
              mb: 2,
              textTransform: "none",
              "&:hover": { backgroundColor: "#4a7a32" },
            }}
          >
            Confirm
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
}

export default CreateProfileViewPage;
